import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import MetricSelectEntryPoint, {
	type MetricSelectEntryPointProps,
} from '@databox/ember-integrations/lib/components/MetricSelect/MetricSelectEntryPoint';
import type { ReactNode } from '@databox/ember-integrations/lib/components/Setup';
import {
	MetricRegistryExclude,
	MetricRegistryScope,
} from '@databox/types/domains/metric/metric-registry.enums';

import type { PopupData, ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode, isAlive } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

type MetricSelectOptions = NonNullable<MetricSelectEntryPointProps['options']>;

// The calculation editor can't take YouTube metrics (`disableYouTubeMetrics`, `scripts/form/popup-options-metric`).
const YOUTUBE_SOURCE_TYPE_KEY = 'YouTube';

// Up to 880×700, shrinking with the window (full width on a phone), styled in popups.less.
export const METRIC_SELECT_POPUP_CLASS = 'popup-metric-select-react';

/**
 * The Metric Select: a chromeless Ember popup around the React popup (`MetricSelectEntryPoint`), so it stacks with
 * the Ember popups opened from it (Connect, the custom metric builder, billing). React draws everything, the close
 * button included; the flows run through `metricRegistryService.createMetricSelectHost`. Client performance's header
 * lists the catalogue (`catalogueOnly`) and its cells a client space (`clientSpaceId`).
 */
export default class FormMetricSelectPopupComponent extends PopupInnerModalComponent<PopupData.Form.MetricSelect> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-registry') declare metricRegistryService: Service.MetricRegistry;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	// Built once: React keeps the host as a context value and callback dependency.
	@cached
	get host(): MetricSelectEntryPointProps['host'] {
		return this.metricRegistryService.createMetricSelectHost(this.popupData, {
			close: () => {
				void this.close();
			},
			reposition: () => {
				this.reposition();
			},
			isOpen: () => isAlive(this) && this.args.model.isOpen,
		});
	}

	get entryPointProps(): MetricSelectEntryPointProps {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;
		const { metric, skipCreateCustomMetrics, catalogueOnly, clientSpaceId } = this.popupData;

		return {
			// The designer opens it dark whatever the app's theme.
			isDarkThemeActive:
				this.args.model.theme === 'dark' || this.navigationService.theme.isDark,
			accessToken: accessToken ?? '',
			dataToken: dataToken ?? '',
			spaceId: this.accountService.id,
			user: {
				id: this.currentUserService.numericId,
				name: this.currentUserService.displayName,
			},
			host: this.host,
			selected: metric ? { key: metric.key, spaceAccessId: metric.spaceAccessId } : null,
			scope: catalogueOnly
				? MetricRegistryScope.CATALOGUE
				: MetricRegistryScope.METRIC_SELECT,
			spaceIds: clientSpaceId ? [clientSpaceId] : undefined,
			options: {
				exclude: this.exclude,
				disableUnconnected: this.popupData.disableNotConnected,
				disabledSourceTypeKeys: this.popupData.disableYouTubeMetrics
					? [YOUTUBE_SOURCE_TYPE_KEY]
					: undefined,
				hideConnectBtn: this.popupData.hideConnectBtn,
				hideNoResultsActions: this.popupData.hideNoResultsActions,
				hideCreateCustomMetric: skipCreateCustomMetrics,
				hideTags: this.popupData.hideTags,
			},
			isDemo: this.currentUserService.isDemo,
			isMobileApp: this.navigationService.isMobileApp,
		};
	}

	// The row kinds the popup leaves out. Goals cost the BE a separate analytics call, so they're out unless the opener
	// asks; a client performance cell lists the client's own metrics only (BE M2 answers).
	get exclude(): NonNullable<MetricSelectOptions['exclude']> {
		const { includeGoals, clientSpaceId, skipHorizon } = this.popupData;

		return [
			...(includeGoals ? [] : [MetricRegistryExclude.GOAL]),
			...(clientSpaceId
				? [MetricRegistryExclude.UNCONNECTED, MetricRegistryExclude.TEMPLATE]
				: []),
			...(skipHorizon ? [MetricRegistryExclude.HORIZON] : []),
		];
	}

	@action
	setupView(node: HTMLElement): void {
		mountReactRoot(this, node, () => this.renderMetricSelect());
	}

	@action
	teardownView(): void {
		unmountReactRoot(this);
	}

	private renderMetricSelect(): void {
		if (this.reactElement) {
			// @types/react@19's FunctionComponent returns ReactNode | Promise<ReactNode>;
			// reactService.render only accepts ReactNode.
			this.reactService.render(
				this.reactElement,
				MetricSelectEntryPoint(this.entryPointProps) as ReactNode,
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			class="metric-select-popup__body"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		/>
	</template>
}
